
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, User, ArrowRight, TrendingUp, ShieldCheck, Camera } from 'lucide-react';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallModal, setShowInstallModal] = useState(false);

  useEffect(() => {
    // Force light mode on login screen
    document.documentElement.classList.remove('dark');
    localStorage.setItem('theme', 'light');

    const handler = (e: any) => {
      e.preventDefault();
      (window as any).deferredPrompt = e;
      setDeferredPrompt(e);
      setShowInstallModal(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    if ((window as any).deferredPrompt) {
      setDeferredPrompt((window as any).deferredPrompt);
      setShowInstallModal(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult: any) => {
        setDeferredPrompt(null);
        setShowInstallModal(false);
      });
    }
  };
  
  const { login, isAuthenticated, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (!isLoading && isAuthenticated && user) {
      if (user.role === 'celular') {
        navigate('/mobile-portal', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, user, navigate, from]);

  const headerText = localStorage.getItem('app_header_text') || 'Gestão Fácil';
  const headerSubtitle = localStorage.getItem('app_header_subtitle') || 'Painel Web';
  const logoUrl = localStorage.getItem('report_logo_url');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    
    try {
      const result = await login(username, password);
      if (result.success) {
        if (result.user?.role === 'celular') {
          navigate('/mobile-portal', { replace: true });
        } else {
          navigate(from, { replace: true });
        }
      } else {
        setError(result.error || 'Credenciais inválidas');
      }
    } catch (err) {
      setError('Erro de conexão.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center font-sans bg-slate-50 relative overflow-hidden">
      
      {/* Background decorative elements */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand-400/20 rounded-full blur-3xl pointer-events-none animate-pulse"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-400/20 rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDelay: '1s' }}></div>

      {/* PWA Install Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-6 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6 text-purple-600">
              <Camera size={32} />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Instalar Aplicativo</h3>
            <p className="text-slate-600 mb-8">Instale nosso portal para um acesso mais rápido e fácil.</p>
            <div className="flex flex-col gap-3">
              <button 
                onClick={handleInstall}
                className="w-full py-4 bg-purple-600 text-white font-bold rounded-xl shadow-lg active:scale-95 transition-all"
              >
                Baixar Aplicativo
              </button>
              <button 
                onClick={() => setShowInstallModal(false)}
                className="w-full py-4 bg-slate-100 text-slate-600 font-bold rounded-xl active:scale-95 transition-all"
              >
                Continuar na Web
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Centered Container */}
      <div className="w-full max-w-4xl flex flex-col md:flex-row bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-700 m-4 relative z-10">
        
        {/* Presentation Side */}
        <div className="md:w-1/2 bg-brand-700 p-10 flex flex-col justify-center text-white relative overflow-hidden">
           <div className="absolute inset-0 bg-[url('https://picsum.photos/seed/business/800/800')] opacity-10 bg-cover bg-center mix-blend-overlay"></div>
           <div className="relative z-10">
              {logoUrl ? (
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-8 shadow-lg transform -rotate-6 overflow-hidden">
                  <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
                </div>
              ) : (
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-brand-700 mb-8 shadow-lg transform -rotate-6">
                    <TrendingUp size={32} />
                </div>
              )}
              <h1 className="text-4xl font-extrabold mb-1 tracking-tight">{headerText}</h1>
              <p className="text-brand-200 font-medium uppercase tracking-widest text-sm mb-6">{headerSubtitle}</p>
              <p className="text-brand-100 text-lg leading-relaxed">
                Gerencie suas finanças com eficiência, segurança e uma interface intuitiva. O controle que sua empresa precisa, simplificado.
              </p>
           </div>
        </div>

        {/* Login Form Side */}
        <div className="md:w-1/2 p-10 flex flex-col justify-center">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">Bem-vindo de volta</h2>
            <p className="text-slate-500 text-sm">Acesse sua conta para continuar</p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 p-3 text-red-700 text-sm rounded-lg flex items-start gap-3 animate-in slide-in-from-top-2">
              <ShieldCheck size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider ml-1">Usuário</label>
              <div className="relative group">
                <User className="absolute left-3 top-3 text-slate-400 group-focus-within:text-brand-600 transition-colors" size={18} />
                <input 
                  type="text" 
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 bg-slate-50 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm text-slate-800 font-medium placeholder:text-slate-400"
                  placeholder="Digite seu usuário"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider ml-1">Senha</label>
              <div className="relative group">
                <Lock className="absolute left-3 top-3 text-slate-400 group-focus-within:text-brand-600 transition-colors" size={18} />
                <input 
                  type="password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 bg-slate-50 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm text-slate-800 font-medium placeholder:text-slate-400"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full bg-brand-600 hover:bg-brand-700 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-brand-600/20 transition-all flex items-center justify-center gap-2 text-sm mt-4 disabled:opacity-70 disabled:cursor-not-allowed hover:-translate-y-0.5"
            >
              {isSubmitting ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              ) : (
                <>Entrar no Sistema <ArrowRight size={16} /></>
              )}
            </button>
            <p className="text-center text-xs text-slate-400 mt-4 flex items-center justify-center gap-1.5">
              <ShieldCheck size={14} className="text-brand-500" />
              Sessão salva em cache para manter você conectado
            </p>
          </form>
        </div>

        {/* Footer Signature */}
        <div className="mt-6 text-center text-xs text-slate-400 select-none">
          <p className="flex flex-wrap items-center justify-center gap-1.5">
            <span>2026 Gestão Fácil ERP ®</span>
            <span>•</span>
            <span>Todos os direitos reservados.</span>
            <span>•</span>
            <span>
              Desenvolvido por{' '}
              <a 
                href="http://alonedev.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-semibold text-purple-600 hover:underline"
              >
                AloneDev.com ®
              </a>
            </span>
            <span>•</span>
            <span>Todos os direitos reservados.</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
